import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Table } from 'antd';
import MapGL, { Marker } from 'react-map-gl';
import WebMercatorViewport from 'viewport-mercator-project';
import 'mapbox-gl/dist/mapbox-gl.css';
import styles from './FuelTransactionMap.styles';
import locationFromString from '../../utils/locationFromString';
import { truncateGeoLocation } from '../../utils';
import LinkToGoogleMap from '../shared/LinkToGoogleMap';
import { selectSelectedFuelTransaction } from '../../redux/store/userData/fuelTransactions';

const MARKER_WIDTH = 42;
const MARKER_HEIGHT = 50;
const MAX_ZOOM = 17;
const STATION_ICON = '/images/map/gas-station-marker.svg';
const DRIVER_ICON = '/images/map/driver-marker.svg';
const CAR_ICON = '/images/car-marker/car-marker-released.svg';

type MapMarker = {
  key: string;
  position: string;
  icon: string;
  label: string;
};

export const hasFuelTransactionLocations = (fuelTransaction: any) =>
  !!(
    fuelTransaction?.stationLocation ||
    fuelTransaction?.userLocation ||
    fuelTransaction?.carLocation ||
    fuelTransaction?.carLocationAtCompletion
  );

const FuelTransactionMap: React.FC = () => {
  const intl = useIntl();
  const fuelTransaction = useSelector(selectSelectedFuelTransaction);
  const containerRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState<any>(null);
  const {
    stationLocation,
    userLocation,
    carLocation,
    carLocationAtCompletion,
  } = fuelTransaction || {};

  const label = (id: string, defaultMessage: string) =>
    intl.formatMessage({ id, defaultMessage });
  const stationLabel = label(
    'fleetadmin.fuelTransactions.map.station',
    'Gas station'
  );
  const driverLabel = label(
    'fleetadmin.ryd.userLocation',
    'Initial Driver location'
  );
  const carBeforeLabel = label(
    'fleetadmin.fuelTransactions.map.carBefore',
    'Car before'
  );
  const carAfterLabel = label(
    'fleetadmin.fuelTransactions.map.carAfter',
    'Car after'
  );

  const carNotMoved = !!carLocation && carLocation === carLocationAtCompletion;
  const markers = [
    stationLocation && {
      key: 'station',
      position: stationLocation,
      icon: STATION_ICON,
      label: stationLabel,
    },
    userLocation && {
      key: 'driver',
      position: userLocation,
      icon: DRIVER_ICON,
      label: driverLabel,
    },
    carLocation && {
      key: 'carBefore',
      position: carLocation,
      icon: CAR_ICON,
      label: carNotMoved
        ? label(
            'fleetadmin.fuelTransactions.map.carBeforeAndAfter',
            'Car before & after'
          )
        : carBeforeLabel,
    },
    carLocationAtCompletion &&
      !carNotMoved && {
        key: 'carAfter',
        position: carLocationAtCompletion,
        icon: CAR_ICON,
        label: carAfterLabel,
      },
  ].filter(Boolean) as MapMarker[];

  const locationRows = [
    {
      key: 'car',
      label: label('fleetadmin.fuelTransactions.map.car', 'Car'),
      atStart: carLocation,
      atCompletion: carLocationAtCompletion,
    },
    {
      key: 'driver',
      label: label('fleetadmin.fuelTransactions.map.driver', 'Driver'),
      atStart: userLocation,
    },
    { key: 'station', label: stationLabel, atStart: stationLocation },
  ].filter(({ atStart, atCompletion }) => atStart || atCompletion);

  const renderPosition = (position?: string) => (
    <LinkToGoogleMap
      position={position}
      label={position && truncateGeoLocation(position, 4)}
    />
  );
  const locationColumns = [
    {
      title: label('fleetadmin.fuelTransactions.map.location', 'Location'),
      dataIndex: 'label',
    },
    {
      title: label(
        'fleetadmin.fuelTransactions.map.atStart',
        'At fueling start'
      ),
      dataIndex: 'atStart',
      render: renderPosition,
    },
    {
      title: label(
        'fleetadmin.fuelTransactions.map.atCompletion',
        'At completion'
      ),
      dataIndex: 'atCompletion',
      render: renderPosition,
    },
  ];

  useEffect(() => {
    const container = containerRef.current;
    const points = markers.map(({ position }) => locationFromString(position)!);
    if (!container || !points.length) return;
    const lngs = points.map(({ lng }) => lng);
    const lats = points.map(({ lat }) => lat);
    const { longitude, latitude, zoom } = new WebMercatorViewport({
      width: container.offsetWidth,
      height: container.offsetHeight,
    }).fitBounds(
      [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ],
      {
        padding: { top: MARKER_HEIGHT + 20, bottom: 20, left: 40, right: 140 },
      }
    );
    setViewport({ longitude, latitude, zoom: Math.min(zoom, MAX_ZOOM) });
  }, [stationLocation, userLocation, carLocation, carLocationAtCompletion]);

  return (
    <>
      <div
        ref={containerRef}
        className="fuel-transaction-map"
        data-testid="FuelTransactionMap"
      >
        {viewport && (
          <MapGL
            {...viewport}
            width="100%"
            height="100%"
            onViewportChange={setViewport}
            mapboxApiAccessToken={process.env.VITE_MAPBOX_KEY}
            dragRotate={false}
            touchRotate={false}
            attributionControl={false}
          >
            {markers.map(({ key, position, icon, label }) => {
              const { lat, lng } = locationFromString(position)!;
              return (
                <Marker
                  key={key}
                  latitude={lat}
                  longitude={lng}
                  offsetTop={-MARKER_HEIGHT}
                  offsetLeft={-MARKER_WIDTH / 2}
                >
                  <div className="fuel-transaction-map-marker">
                    <img
                      src={icon}
                      width={MARKER_WIDTH}
                      height={MARKER_HEIGHT}
                      alt={label}
                    />
                    <span className="fuel-transaction-map-label">{label}</span>
                  </div>
                </Marker>
              );
            })}
          </MapGL>
        )}
      </div>
      <div className="fuel-transaction-map-locations">
        <Table
          size="small"
          pagination={false}
          columns={locationColumns}
          dataSource={locationRows}
        />
      </div>
      <style jsx>{styles}</style>
    </>
  );
};

export default FuelTransactionMap;
